import * as React from "react";
import { LayoutProvider } from "./LayoutContext";
import * as s from "./Layout.style.css";
import type { LayoutProps } from "./Layout.types";
import { LAYOUT_MOBILE_HEADER_HEIGHT, useIsLayoutMobile } from "../../hooks/useLayoutMobile";

export type { LayoutProps } from "./Layout.types";
export { useLayoutContext } from "./LayoutContext";

export const Layout = ({
  defaultNavBarOpen = true,
  navigationBar,
  contextPanel,
  footer,
  footerHeight = 36,
  children,
}: LayoutProps) => {
  const isMobile = useIsLayoutMobile();
  const [navBarOpen, setNavBarOpen] = React.useState(defaultNavBarOpen);
  const [navWidth, setNavWidth] = React.useState(defaultNavBarOpen ? 220 : 56);
  const [contextPanelWidth, setContextPanelWidth] = React.useState(0);

  const renderedNavigationBar = React.useMemo(() => {
    if (!navigationBar || !React.isValidElement(navigationBar)) return navigationBar;
    return React.cloneElement(
      navigationBar as React.ReactElement<{
        open?: boolean;
        onOpenChange?: (o: boolean) => void;
        onWidthChange?: (w: number) => void;
      }>,
      {
        open: navBarOpen,
        onOpenChange: setNavBarOpen,
        onWidthChange: setNavWidth,
      }
    );
  }, [navigationBar, navBarOpen]);

  const renderedContextPanel = React.useMemo(() => {
    if (!contextPanel || !React.isValidElement(contextPanel)) return contextPanel;
    return React.cloneElement(
      contextPanel as React.ReactElement<{ onWidthChange?: (w: number) => void }>,
      { onWidthChange: setContextPanelWidth }
    );
  }, [contextPanel]);

  const contextValue = React.useMemo(
    () => ({
      navWidth: isMobile ? 0 : navWidth,
      footerHeight: footer ? footerHeight : 0,
      isMobile,
    }),
    [navWidth, footerHeight, isMobile, footer]
  );

  const rootStyle = React.useMemo(
    () =>
      ({
        "--nav-width": `${isMobile ? 0 : navWidth}px`,
        "--context-panel-width": `${contextPanelWidth}px`,
        "--footer-height": footer && !isMobile ? `${footerHeight}px` : "0px",
        "--header-height": isMobile ? `${LAYOUT_MOBILE_HEADER_HEIGHT}px` : "0px",
      } as React.CSSProperties),
    [navWidth, contextPanelWidth, footerHeight, isMobile, footer]
  );

  return (
    <LayoutProvider value={contextValue}>
      <div className={s.layoutRoot} style={rootStyle}>
        {renderedNavigationBar}
        {renderedContextPanel}
        <main className={s.layoutMain}>{children}</main>
        {footer}
      </div>
    </LayoutProvider>
  );
};
